import React, { useCallback, useEffect, useState } from "react";
import {
  getReviewPolicy,
  updateReviewPolicy,
  fetchAuthUsers,
  getApproverGroups,
  createApproverGroup,
  updateApproverGroup,
  deleteApproverGroup,
  setApproverGroupMembers,
} from "../../services/api";
import { setTransientMessage } from "./adminUtils";
import LoadingButton from "../common/LoadingButton";

const ReviewWorkflowPolicyManagement = ({
  user,
  hideHeader = false,
  mapLanguage = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsSaving, setGroupsSaving] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [users, setUsers] = useState([]);
  const [ruleForm, setRuleForm] = useState({
    name: "",
    description: "",
    approval_mode: "ANY_ONE",
    active: true,
    members: [],
  });
  const [policy, setPolicy] = useState({
    enable_l2_approval: false,
    extractor_cannot_approve_own: false,
    require_reason_on_reject: true,
    require_reason_on_request_changes: true,
    updated_by: "",
    updated_at: "",
  });

  const canEdit = Boolean(
    user && (user.role === "Administrator" || user.is_super_user)
  );

  const loadPolicy = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getReviewPolicy();
      setPolicy((prev) => ({
        ...prev,
        enable_l2_approval: Boolean(data?.enable_l2_approval),
        extractor_cannot_approve_own: Boolean(
          data?.extractor_cannot_approve_own
        ),
        require_reason_on_reject: Boolean(data?.require_reason_on_reject),
        require_reason_on_request_changes: Boolean(
          data?.require_reason_on_request_changes
        ),
        updated_by: data?.updated_by || "",
        updated_at: data?.updated_at || "",
      }));
      if (data?._not_configured) {
        setNotice(
          data?._message ||
            "No policies have been configured yet. Save to create the first policy."
        );
      } else {
        setNotice(null);
      }
    } catch (e) {
      const msg = e?.message || "Failed to load approval rules";
      if (String(msg).toLowerCase() === "not found") {
        setError(null);
        setNotice(
          "No policies have been configured yet. Save to create the first policy."
        );
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const loadGroups = useCallback(async () => {
    try {
      setGroupsLoading(true);
      setGroupsError(null);
      const data = await getApproverGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      setGroupsError(e?.message || "Failed to load approver groups");
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAuthUsers();
        if (!cancelled) setUsers(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    if (!canEdit) return;
    try {
      setSaving(true);
      setError(null);
      setNotice(null);
      await updateReviewPolicy(policy);
      await loadPolicy();
      setTransientMessage(setNotice, "Review rules saved.");
    } catch (e) {
      setError(e?.message || "Failed to save approval rules");
    } finally {
      setSaving(false);
    }
  };

  const resetRuleForm = () => {
    setEditingGroupId(null);
    setRuleForm({
      name: "",
      description: "",
      approval_mode: "ANY_ONE",
      active: true,
      members: [],
    });
  };

  const handleSaveRule = async () => {
    if (!canEdit) return;
    try {
      setGroupsSaving(true);
      setGroupsError(null);
      const payload = {
        name: ruleForm.name,
        description: ruleForm.description,
        approval_mode: ruleForm.approval_mode,
        active: Boolean(ruleForm.active),
      };
      let groupId = editingGroupId;
      if (editingGroupId) {
        await updateApproverGroup(editingGroupId, payload);
      } else {
        const created = await createApproverGroup(payload);
        groupId = created?.id;
      }
      if (groupId) {
        await setApproverGroupMembers(
          groupId,
          (Array.isArray(ruleForm.members) ? ruleForm.members : [])
            .map((m) => String(m ?? "").trim())
            .filter(Boolean)
        );
      }
      await loadGroups();
      resetRuleForm();
      setTransientMessage(
        setNotice,
        editingGroupId ? "Approver group updated." : "Approver group created."
      );
    } catch (e) {
      setGroupsError(e?.message || "Failed to save approver group");
    } finally {
      setGroupsSaving(false);
    }
  };

  const handleEditRule = (rule) => {
    setEditingGroupId(rule.id);
    setRuleForm({
      name: rule.name || "",
      description: rule.description || "",
      approval_mode: rule.approval_mode || "ANY_ONE",
      active: Boolean(rule.active),
      members: Array.isArray(rule.members)
        ? rule.members.map((m) => m.user_id || m.id).filter(Boolean)
        : [],
    });
  };

  const handleDeleteRule = async (ruleId) => {
    if (!canEdit) return;
    try {
      setGroupsSaving(true);
      setGroupsError(null);
      await deleteApproverGroup(ruleId);
      await loadGroups();
      if (editingGroupId === ruleId) resetRuleForm();
      setTransientMessage(setNotice, "Approver group deleted.");
    } catch (e) {
      setGroupsError(e?.message || "Failed to delete approver group");
    } finally {
      setGroupsSaving(false);
    }
  };

  return (
    <div className="w-full p-4">
      <div className="max-w-5xl">
        {!hideHeader ? (
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white mb-1">
              {mapLanguage ? "Review before publish" : "Review Workflow Policy"}
            </h1>
            <p className="text-gray-400 text-sm">
              {mapLanguage
                ? "Choose who must approve AI-proposed and imported items before they become part of your live enterprise map."
                : "Configure approval behavior for Architecture Review workflows."}
            </p>
          </div>
        ) : null}

        <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-200">General rules</h2>
            <div className="flex items-center gap-2">
              <LoadingButton
                type="button"
                onClick={loadPolicy}
                loading={loading}
                loadingLabel="Loading..."
                disabled={saving}
                className="text-xs px-2 py-1 rounded bg-gray-800 text-gray-200 border border-gray-600 hover:bg-gray-700 disabled:opacity-50"
              >
                Refresh
              </LoadingButton>
              <LoadingButton
                type="button"
                onClick={handleSave}
                loading={saving}
                loadingLabel="Saving..."
                disabled={!canEdit || loading}
                className="text-xs px-2 py-1 rounded bg-primary-600 text-white border border-primary-500/70 hover:bg-primary-700 disabled:opacity-50"
              >
                Save policy
              </LoadingButton>
            </div>
          </div>

          {!canEdit && (
            <p className="mt-2 text-xs text-amber-300">
              You have read-only access. Only administrators can change this
              policy.
            </p>
          )}
          {error && <div className="mt-2 text-xs text-red-300">{error}</div>}
          {notice && !error && (
            <div className="mt-2 text-xs text-emerald-300">{notice}</div>
          )}

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <label className="flex items-center gap-2 text-gray-200">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={policy.enable_l2_approval}
                onChange={(e) =>
                  setPolicy((prev) => ({
                    ...prev,
                    enable_l2_approval: e.target.checked,
                  }))
                }
              />
              Require a second approver
            </label>
            <label className="flex items-center gap-2 text-gray-200">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={policy.extractor_cannot_approve_own}
                onChange={(e) =>
                  setPolicy((prev) => ({
                    ...prev,
                    extractor_cannot_approve_own: e.target.checked,
                  }))
                }
              />
              Extractor cannot approve own items
            </label>
            <label className="flex items-center gap-2 text-gray-200">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={policy.require_reason_on_reject}
                onChange={(e) =>
                  setPolicy((prev) => ({
                    ...prev,
                    require_reason_on_reject: e.target.checked,
                  }))
                }
              />
              Require reason on reject
            </label>
            <label className="flex items-center gap-2 text-gray-200">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={policy.require_reason_on_request_changes}
                onChange={(e) =>
                  setPolicy((prev) => ({
                    ...prev,
                    require_reason_on_request_changes: e.target.checked,
                  }))
                }
              />
              Require reason on request changes
            </label>
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            When both gates above are off, Add-to-map from enrichment review lands
            as Approved (on the shared map). Turn either on to keep Proposed
            until formal baseline approval.
          </p>

          <p className="mt-3 text-[11px] text-gray-500">
            Last updated:{" "}
            {policy.updated_at
              ? new Date(policy.updated_at).toLocaleString()
              : "—"}
            {policy.updated_by ? ` by ${policy.updated_by}` : ""}
          </p>
        </div>

        <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 mt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-200">
              Approver groups
            </h2>
            <div className="flex items-center gap-2">
              <LoadingButton
                type="button"
                onClick={loadGroups}
                loading={groupsLoading}
                loadingLabel="Loading..."
                disabled={groupsSaving}
                className="text-xs px-2 py-1 rounded bg-gray-800 text-gray-200 border border-gray-600 hover:bg-gray-700 disabled:opacity-50"
              >
                Refresh groups
              </LoadingButton>
            </div>
          </div>
          <p className="text-gray-400 text-xs mt-1">
            Groups used when editors submit items from Your map. Assign members and
            choose whether any one member or all members must approve.
          </p>
          {groupsError && (
            <div className="mt-2 text-xs text-red-300">{groupsError}</div>
          )}

          <div className="mt-3 space-y-2">
            {groups.map((r) => (
              <div
                key={r.id}
                className="rounded border border-gray-700 bg-gray-900/40 px-3 py-2 flex flex-wrap items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <p className="text-sm text-gray-100 font-medium truncate">{r.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {r.approval_mode === "ALL_MEMBERS"
                      ? "All members must approve"
                      : "Any one member can approve"}
                    {" • "}
                    {Array.isArray(r.members) && r.members.length > 0
                      ? `${r.members.length} member(s)`
                      : "No members"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => handleEditRule(r)}
                    className="px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-xs"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={!canEdit || groupsSaving}
                    onClick={() => handleDeleteRule(r.id)}
                    className="px-2 py-0.5 rounded bg-rose-900/80 hover:bg-rose-800 disabled:opacity-50 text-xs"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {groups.length === 0 && !groupsLoading ? (
              <div className="text-xs text-gray-500 py-2">
                No approver groups configured yet.
              </div>
            ) : null}
          </div>

          <div className="mt-4 rounded border border-gray-700 p-3">
            <div className="text-xs font-semibold text-gray-300 mb-2">
              {editingGroupId ? "Edit group" : "Add group"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <input
                value={ruleForm.name}
                onChange={(e) => setRuleForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Group name"
                className="rounded border border-gray-600 bg-gray-900 text-gray-100 px-2 py-1"
              />
              <select
                value={ruleForm.approval_mode}
                onChange={(e) =>
                  setRuleForm((p) => ({ ...p, approval_mode: e.target.value }))
                }
                className="rounded border border-gray-600 bg-gray-900 text-gray-100 px-2 py-1"
              >
                <option value="ANY_ONE">Any one member can approve</option>
                <option value="ALL_MEMBERS">All members must approve</option>
              </select>
              <input
                value={ruleForm.description}
                onChange={(e) =>
                  setRuleForm((p) => ({ ...p, description: e.target.value }))
                }
                placeholder="Description (optional)"
                className="rounded border border-gray-600 bg-gray-900 text-gray-100 px-2 py-1"
              />
              <label className="text-gray-300 text-[11px] sm:col-span-2">
                Members
              </label>
              <select
                multiple
                value={ruleForm.members}
                onChange={(e) =>
                  setRuleForm((p) => ({
                    ...p,
                    members: Array.from(e.target.selectedOptions).map(
                      (o) => o.value
                    ),
                  }))
                }
                className="rounded border border-gray-600 bg-gray-900 text-gray-100 px-2 py-1 sm:col-span-2 min-h-[110px]"
              >
                {users.map((u) => (
                  <option
                    key={u.id || u.user_id || u.email}
                    value={u.id || u.user_id || ""}
                  >
                    {u.name || u.email || u.id || u.user_id} ({u.email || "no-email"})
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-200">
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={ruleForm.active}
                  onChange={(e) =>
                    setRuleForm((p) => ({ ...p, active: e.target.checked }))
                  }
                />
                Enabled
              </label>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                disabled={!canEdit || groupsSaving}
                onClick={handleSaveRule}
                className="text-xs px-3 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {groupsSaving
                  ? "Saving..."
                  : editingGroupId
                    ? "Update group"
                    : "Create group"}
              </button>
              {editingGroupId && (
                <button
                  type="button"
                  disabled={groupsSaving}
                  onClick={resetRuleForm}
                  className="text-xs px-3 py-1 rounded bg-gray-700 text-gray-100 hover:bg-gray-600"
                >
                  Cancel edit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewWorkflowPolicyManagement;

